<script lang="ts">
	import { getContext } from "svelte";
	import { SvelteSet } from "svelte/reactivity";
	import { BundleType, ConversionType, type AssetRef } from "#gen/bundles/v1/bundle_pb.js";
	import { ContactDataSchema, type ContactData } from "#gen/bundles/v1/contact_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import { PostalAddressSchema, type PostalAddress } from "#gen/google/type/postal_address_pb.js";
	import { formattedAddress } from "#lib/address.js";
	import { downloadBlob } from "#lib/blob.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { encode } from "#lib/components/flow/editor/content/contact/parser.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import type { IconAsset } from "#lib/iconProvider.js";
	import { formatPhoneNumber } from "#lib/phonenumber.js";
	import { fromJson, type JsonValue } from "#lib/protobuf-compat.js";
	import type { SuiteStore } from "#lib/stores/suite.js";
	import { booleanValue, listValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { JsonObject } from "@bufbuild/protobuf";
	import type { Writable } from "svelte/store";

	type Appearance = "icons" | "cards";

	interface Item {
		type: string;
		value: string | JsonObject;
	}

	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");
	const suite: SuiteStore<ContactData> = getContext("suite");
	const assetRef: Writable<AssetRef> = getContext("assetRef");
	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");

	const { id, style, page, mode, readOnly = false }: ComponentProps = $props();

	const config = $derived(structValue($page.config, id) ?? {});
	const disabled = $derived(booleanValue(config, "disabled"));
	const appearance = $derived((stringValue(config, "appearance") as Appearance) ?? "cards");
	const items = $derived(listValue(config, "items")?.map(val => val as unknown as Item) ?? []);
	const data = $derived(($suite.bundleType === BundleType.CONTACT ? $suite.data?.type.value : getData(items)) as ContactData);
	const isEditing = $derived(mode === Mode.EDITING);

	const typeIcons: Map<string, IconAsset> = new Map([
		["work_number", { icon: "phone", iconSet: "heroicons" }],
		["mobile_number", { icon: "device-phone-mobile", iconSet: "heroicons" }],
		["email", { icon: "envelope", iconSet: "heroicons" }],
		["website", { icon: "globe-americas", iconSet: "heroicons" }],
		["address", { icon: "map-pin", iconSet: "heroicons" }],
	]);
	const previewTypes: Set<string> = new SvelteSet(["address", "email", "mobile_number", "work_number", "website"]);

	function parseAddress(value: string | JsonObject): PostalAddress | undefined {
		let json: JsonValue | undefined;
		if (typeof value === "string") {
			try {
				const parsed = JSON.parse(value);
				if (parsed && typeof parsed === "object") {
					json = parsed as JsonValue;
				}
			} catch {
				// Unresolved template or plain string, not a structured address.
			}
		} else if (value && typeof value === "object") {
			json = value as JsonValue;
		}
		if (json === undefined) {
			return undefined;
		}
		try {
			return fromJson(PostalAddressSchema, json);
		} catch {
			return undefined;
		}
	}

	function hasValue(item: Item): boolean {
		if (!item.value || !previewTypes.has(item.type)) {
			return false;
		}
		if (typeof item.value === "object") {
			return Object.values(item.value).some(value => Boolean(value));
		}
		return Boolean(item.value);
	}

	function getItemUrl(item: Item): string {
		switch (item.type) {
			case "work_number":
			case "mobile_number":
				return `tel:${item.value}`;
			case "email":
				return `mailto:${item.value}`;
			case "website":
				return item.value as string;
			case "address": {
				const address = parseAddress(item.value);
				if (!address) {
					return "";
				}
				return `https://www.google.com/maps/search/?api=1&query=${formattedAddress(address)}`;
			}
			default:
				return "";
		}
	}

	function formatItem(item: Item): string {
		switch (item.type) {
			case "work_number":
			case "mobile_number":
				return formatPhoneNumber(item.value as string);
			case "address": {
				const address = parseAddress(item.value);
				return address ? formattedAddress(address) : "";
			}
			default:
				return (item.value as string) ?? "";
		}
	}

	function getItemTokenText(item: Item): string {
		return typeof item.value === "string" ? item.value : "";
	}

	function shouldShowTokenizedItem(item: Item): boolean {
		const text = getItemTokenText(item);
		return isEditing && text !== "" && $tokenProvider.hasSubstitutionToken(text);
	}

	function getNonExportedFields(config: JsonObject | undefined): Set<string> {
		const list = (listValue(config, "nonExportedFields") as string[]) ?? [];
		return new SvelteSet(list);
	}

	async function downloadVcard() {
		try {
			const success = await execute(ConversionType.CONTACT_DOWNLOADED, id);
			if (!success) {
				return;
			}
			const vcard = await encode(data, getNonExportedFields(config));
			const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
			const url = URL.createObjectURL(blob);
			downloadBlob(`${data.fullName}.vcf`, url);
		} catch (_: unknown) {
			// TODO: Use the error here to show a helpful message to the user
		}
	}

	function getData(items: Item[]): ContactData {
		return fromJson(ContactDataSchema, Object.assign({}, ...items.map(item => (item.type === "address" && typeof item.value === "string" ? {} : { [item.type]: item.value }))));
	}
</script>

<div class="flex flex-col gap-4" {style}>
	{#if !booleanValue(config, "shareDisabled") !== false || disabled}
		<Button data-action-name="add-to-contacts" size="md" color="none" class="btn-user-primary border border-gray-300 bg-white text-gray-900 hover:bg-gray-100 disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" disabled={readOnly} on:click={downloadVcard}>
			{$assetRef?.shortName || "Add to contacts"}
		</Button>
	{/if}
	<div class="flex justify-between gap-x-2 gap-y-3" class:flex-col={appearance === "cards"}>
		{#each items.filter(hasValue) as item (item.type)}
			{@const icon = typeIcons.get(item.type)}
			{#if appearance === "icons"}
				<Button data-action-name="contact-info-icon-link" on:click={() => window.open(getItemUrl(item))} color="none" class="btn-user-secondary disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" disabled={readOnly}>
					<Icon iconSet={icon?.iconSet} name={icon?.icon} class="h-5 w-5" />
				</Button>
			{:else}
				<Button data-action-name="contact-info-text-link" on:click={() => window.open(getItemUrl(item))} color="none" class="flex items-center gap-2 truncate rounded-2xl border border-gray-300 bg-white p-2.5 disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" disabled={readOnly}>
					<div class="flex h-8 w-8 items-center justify-center overflow-clip rounded-lg p-1.5" style="color: var(--secondary-button-color); background-color: var(--secondary-button-background-color);">
						<Icon iconSet={icon?.iconSet} name={icon?.icon} class="h-5 w-5" />
					</div>
					<P align="left" weight="semibold" size="sm" class="grow truncate p-0.5 text-gray-900">
						<TokenizedText provider={$tokenProvider} text={shouldShowTokenizedItem(item) ? getItemTokenText(item) : formatItem(item)} {mode} />
					</P>
					<div><Icon name="chevron-right" class="h-5 w-5 text-gray-400" /></div>
				</Button>
			{/if}
		{/each}
	</div>
</div>
